import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import CohortRosterDialog from '../components/segments/CohortRosterDialog';
import LmsRosterSyncDialog from '../components/segments/LmsRosterSyncDialog';
import LearnerSearch from '../components/segments/LearnerSearch';
import {
  Users, Layers, Plus, Edit2, Trash2, Search, Calendar, Clock,
  CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronRight,
  Building, Building2, GraduationCap, FolderOpen, Filter, X, DollarSign, TrendingUp, Eye, EyeOff,
  Upload, Download, FileSpreadsheet, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cohort type options
const COHORT_TYPES = [
  { value: 'ongoing', label: 'Ongoing', description: 'Permanent groups (e.g., Sales Department)', icon: Building },
  { value: 'time_bound', label: 'Time-Bound', description: 'Temporary groups (e.g., Q1 Training)', icon: Calendar },
  { value: 'project', label: 'Project', description: 'Project-based groups', icon: FolderOpen },
  { value: 'custom', label: 'Custom', description: 'Custom grouping', icon: Users }
];

// Program status options
const PROGRAM_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  { value: 'archived', label: 'Archived', color: 'bg-amber-100 text-amber-700' }
];

// Cohort/Program Card Component
const SegmentCard = ({ item, type, onEdit, onDelete, onRoster, cohorts = [], programs = [], selectable = false, selected = false, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);
  
  const cohortType = type === 'cohort' ? COHORT_TYPES.find(t => t.value === item.type) : null;
  const programStatus = type === 'program' ? PROGRAM_STATUSES.find(s => s.value === item.status) : null;
  const Icon = type === 'cohort' ? (cohortType?.icon || Users) : GraduationCap;
  
  return (
    <div
      className={`border rounded-lg bg-white hover:shadow-md transition-shadow ${selected ? 'ring-2 ring-teal-500 border-teal-500' : ''}`}
      data-testid={`${type}-card-${item.id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {selectable && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleSelect?.(item.id)}
                onClick={(e) => e.stopPropagation()}
                className="mt-3 h-4 w-4 accent-teal-600 cursor-pointer"
                aria-label={`Select ${item.name}`}
                data-testid={`${type}-select-${item.id}`}
              />
            )}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              type === 'cohort' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
            }`}>
              <Icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">{item.name}</h4>
                {/* Demo Badge */}
                {item.organization_id === 'demo' && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                    Sample Data
                  </Badge>
                )}
                {item.organization_name && item.organization_id !== 'demo' && (
                  <Badge variant="outline" className="text-xs text-teal-600 border-teal-200 bg-teal-50">
                    {item.organization_name}
                  </Badge>
                )}
                {type === 'cohort' && cohortType && (
                  <Badge variant="outline" className="text-xs">
                    {cohortType.label}
                  </Badge>
                )}
                {type === 'program' && programStatus && (
                  <Badge className={`text-xs ${programStatus.color}`}>
                    {programStatus.label}
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
              )}
              
              {/* Stats */}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                {type === 'cohort' && (
                  <>
                    <span>{item.member_count || 0} data points</span>
                    <span>{item.program_count || 0} programs</span>
                  </>
                )}
                {type === 'program' && (
                  <>
                    <span>{item.cohort_ids?.length || 0} cohorts</span>
                    <span>{item.data_point_count || 0} data points</span>
                    {item.metadata?.total_investment > 0 && (
                      <span className="flex items-center gap-1 text-emerald-600 font-medium">
                        <DollarSign size={12} />
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(item.metadata.total_investment)}
                      </span>
                    )}
                  </>
                )}
              </div>
              
              {/* Linked Cohorts (for programs) */}
              {type === 'program' && item.cohort_ids?.length > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {item.cohort_ids.slice(0, 3).map(cid => {
                    const cohort = cohorts.find(c => c.id === cid);
                    return cohort ? (
                      <Badge key={cid} variant="outline" className="text-xs bg-blue-50">
                        {cohort.name}
                      </Badge>
                    ) : null;
                  })}
                  {item.cohort_ids.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{item.cohort_ids.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Linked Programs (for cohorts) */}
              {type === 'cohort' && item.program_ids?.length > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {item.program_ids.slice(0, 3).map(pid => {
                    const prog = programs.find(p => p.id === pid);
                    return prog ? (
                      <Badge key={pid} variant="outline" className="text-xs bg-purple-50">
                        {prog.name}
                      </Badge>
                    ) : null;
                  })}
                  {item.program_ids.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{item.program_ids.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 ml-2">
            {type === 'cohort' && onRoster && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRoster(item)}
                className="h-8 px-2 text-xs gap-1"
                title="Manage learner roster"
                data-testid={`roster-${type}-${item.id}`}
              >
                <Users size={14} /> Roster
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(item)}
              className="h-8 w-8 p-0"
              data-testid={`edit-${type}-${item.id}`}
            >
              <Edit2 size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(item)}
              className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
              data-testid={`delete-${type}-${item.id}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Cohort Dialog
const CohortDialog = ({ open, onClose, cohort, onSave, organizations = [], programs = [], isSuperAdmin = false, existingCohorts = [] }) => {
  const [form, setForm] = useState({
    name: '',
    type: 'ongoing',
    description: '',
    organization_id: '',
    program_ids: []
  });
  const [saving, setSaving] = useState(false);
  // When a super admin is moving a cohort to a DIFFERENT org, we proactively
  // fetch that org's cohorts so the duplicate-name check catches conflicts the
  // current view doesn't load (e.g., viewer is "Previewing as Seertech" but
  // the target is Cascade).
  const [targetOrgCohorts, setTargetOrgCohorts] = useState([]);
  
  useEffect(() => {
    if (cohort) {
      setForm({
        name: cohort.name || '',
        type: cohort.type || 'ongoing',
        description: cohort.description || '',
        organization_id: cohort.organization_id || '',
        program_ids: cohort.program_ids || []
      });
    } else {
      setForm({ name: '', type: 'ongoing', description: '', organization_id: '', program_ids: [] });
    }
  }, [cohort, open]);

  useEffect(() => {
    // Only super admins moving between orgs need the cross-org check.
    const targetOrgId = form.organization_id;
    if (!isSuperAdmin || !open || !targetOrgId || targetOrgId === cohort?.organization_id) {
      setTargetOrgCohorts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(
          `${API_URL}/api/segments/cohorts?organization_id=${encodeURIComponent(targetOrgId)}&include_demo=false&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTargetOrgCohorts(data.cohorts || []);
      } catch { /* best-effort pre-check; backend remains the source of truth */ }
    })();
    return () => { cancelled = true; };
  }, [form.organization_id, isSuperAdmin, open, cohort?.organization_id]);

  // Union of loaded cohorts (viewer context) + target-org cohorts (for moves).
  const allKnownCohorts = React.useMemo(() => {
    const byId = new Map();
    for (const c of existingCohorts) byId.set(c.id, c);
    for (const c of targetOrgCohorts) byId.set(c.id, c);
    return Array.from(byId.values());
  }, [existingCohorts, targetOrgCohorts]);

  // Client-side duplicate check: warn the user the moment they pick a target org
  // that already has a cohort with this name, instead of round-tripping to the backend.
  const duplicateInTargetOrg = React.useMemo(() => {
    if (!form.name?.trim() || !form.organization_id) return null;
    return allKnownCohorts.find(c =>
      c.id !== cohort?.id &&
      c.organization_id === form.organization_id &&
      (c.name || '').trim().toLowerCase() === form.name.trim().toLowerCase()
    ) || null;
  }, [form.name, form.organization_id, allKnownCohorts, cohort?.id]);
  
  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (duplicateInTargetOrg) {
      const orgName = organizations.find(o => o.id === form.organization_id)?.name || form.organization_id;
      toast.error(`'${orgName}' already has a cohort named '${form.name}'. Rename one of them before moving.`);
      return;
    }
    
    const payload = { name: form.name, type: form.type, description: form.description };
    // Always send organization_id if set — backend enforces permissions
    if (form.organization_id) {
      payload.organization_id = form.organization_id;
    }
    if (form.program_ids.length > 0 || (cohort && cohort.program_ids?.length > 0)) {
      payload.program_ids = form.program_ids;
    }
    
    setSaving(true);
    try {
      await onSave(payload, cohort?.id);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save cohort');
    }
    setSaving(false);
  };
  
  const toggleProgram = (progId) => {
    setForm(prev => ({
      ...prev,
      program_ids: prev.program_ids.includes(progId)
        ? prev.program_ids.filter(id => id !== progId)
        : [...prev.program_ids, progId]
    }));
  };
  
  // Filter programs to same org when editing
  const availablePrograms = programs.filter(p => {
    if (!form.organization_id) return true;
    return p.organization_id === form.organization_id || p.organization_id === 'demo';
  });
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cohort-dialog">
        <DialogHeader>
          <DialogTitle>{cohort ? 'Edit Cohort' : 'Create Cohort'}</DialogTitle>
          <DialogDescription>
            {cohort ? 'Update the cohort details' : 'Create a new cohort to segment your data'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Organization - Super Admin: editable selector, Others: read-only display */}
          {isSuperAdmin && organizations.length > 0 ? (
            <div>
              <Label>Organization</Label>
              <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                <SelectTrigger data-testid="cohort-org-selector">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cohort?.organization_name && (
                <p className="text-xs text-gray-500 mt-1">Currently: {cohort.organization_name}</p>
              )}
              {duplicateInTargetOrg && (
                <div
                  className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2"
                  data-testid="cohort-duplicate-warning"
                >
                  <div className="flex-1">
                    <strong>Name conflict:</strong>{' '}
                    {organizations.find(o => o.id === form.organization_id)?.name || 'Target org'} already
                    has a cohort named <span className="font-mono">"{form.name}"</span>.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs border-amber-400 hover:bg-amber-100 shrink-0"
                    data-testid="cohort-rename-and-move-btn"
                    onClick={() => {
                      const sourceOrgName =
                        cohort?.organization_name ||
                        organizations.find(o => o.id === cohort?.organization_id)?.name ||
                        'Source';
                      const base = form.name.trim();
                      const suffix = ` (from ${sourceOrgName})`;
                      // Ensure the newly proposed name is itself unique in the target org.
                      let candidate = `${base}${suffix}`;
                      const existsInTarget = (n) =>
                        allKnownCohorts.some(c =>
                          c.id !== cohort?.id &&
                          c.organization_id === form.organization_id &&
                          (c.name || '').trim().toLowerCase() === n.trim().toLowerCase()
                        );
                      let i = 2;
                      while (existsInTarget(candidate)) {
                        candidate = `${base}${suffix} ${i}`;
                        i += 1;
                      }
                      setForm(prev => ({ ...prev, name: candidate }));
                      toast.info(`Renamed to "${candidate}". Click Save Changes to move.`);
                    }}
                  >
                    Rename &amp; move
                  </Button>
                </div>
              )}
            </div>
          ) : cohort?.organization_name && (
            <div>
              <Label>Organization</Label>
              <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-gray-50 rounded-lg border" data-testid="cohort-org-display">
                <Building size={14} className="text-teal-500" />
                <span className="text-sm font-medium text-gray-700">{cohort.organization_name}</span>
              </div>
            </div>
          )}
          
          <div>
            <Label htmlFor="cohort-name">Name *</Label>
            <Input
              id="cohort-name"
              placeholder="e.g., Sales Department, Q1 2026 Training"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="cohort-name-input"
            />
          </div>
          
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {COHORT_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setForm({ ...form, type: type.value })}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      form.type === type.value
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <Icon size={16} className={form.type === type.value ? 'text-blue-600' : 'text-gray-400'} />
                    <p className="font-medium text-sm mt-1">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
          
          <div>
            <Label htmlFor="cohort-desc">Description</Label>
            <Textarea
              id="cohort-desc"
              placeholder="Optional description..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>
          
          {/* Linked Programs */}
          {availablePrograms.length > 0 && (
            <div>
              <Label>Link to Programs</Label>
              <p className="text-xs text-gray-500 mb-2">Select which programs apply to this cohort</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {availablePrograms.map(prog => (
                  <label
                    key={prog.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      form.program_ids.includes(prog.id)
                        ? 'bg-purple-50 border border-purple-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.program_ids.includes(prog.id)}
                      onChange={() => toggleProgram(prog.id)}
                      className="rounded border-gray-300"
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <span className="font-medium text-sm">{prog.name}</span>
                      {prog.organization_name && prog.organization_id !== 'demo' && (
                        <Badge variant="outline" className="text-xs text-teal-600 border-teal-200">{prog.organization_name}</Badge>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {cohort ? 'Save Changes' : 'Create Cohort'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create/Edit Program Dialog
const ProgramDialog = ({ open, onClose, program, cohorts, onSave, organizations = [], isSuperAdmin = false, existingPrograms = [] }) => {
  const [form, setForm] = useState({
    name: '',
    type: 'general',
    description: '',
    status: 'draft',
    cohort_ids: [],
    total_investment: '',
    impact_per_percent: '',
    organization_id: ''
  });
  const [saving, setSaving] = useState(false);
  // Cross-org programs list for moves (mirrors CohortDialog)
  const [targetOrgPrograms, setTargetOrgPrograms] = useState([]);
  
  useEffect(() => {
    if (program) {
      setForm({
        name: program.name || '',
        type: program.type || 'general',
        description: program.description || '',
        status: program.status || 'draft',
        cohort_ids: program.cohort_ids || [],
        total_investment: program.metadata?.total_investment || '',
        impact_per_percent: program.metadata?.impact_per_percent || '',
        organization_id: program.organization_id || ''
      });
    } else {
      setForm({ name: '', type: 'general', description: '', status: 'draft', cohort_ids: [], total_investment: '', impact_per_percent: '', organization_id: '' });
    }
  }, [program, open]);

  useEffect(() => {
    const targetOrgId = form.organization_id;
    if (!isSuperAdmin || !open || !targetOrgId || targetOrgId === program?.organization_id) {
      setTargetOrgPrograms([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(
          `${API_URL}/api/segments/programs?organization_id=${encodeURIComponent(targetOrgId)}&include_demo=false&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTargetOrgPrograms(data.programs || []);
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [form.organization_id, isSuperAdmin, open, program?.organization_id]);

  const allKnownPrograms = React.useMemo(() => {
    const byId = new Map();
    for (const p of existingPrograms) byId.set(p.id, p);
    for (const p of targetOrgPrograms) byId.set(p.id, p);
    return Array.from(byId.values());
  }, [existingPrograms, targetOrgPrograms]);

  const duplicateInTargetOrg = React.useMemo(() => {
    if (!form.name?.trim() || !form.organization_id) return null;
    return allKnownPrograms.find(p =>
      p.id !== program?.id &&
      p.organization_id === form.organization_id &&
      (p.name || '').trim().toLowerCase() === form.name.trim().toLowerCase()
    ) || null;
  }, [form.name, form.organization_id, allKnownPrograms, program?.id]);
  
  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (duplicateInTargetOrg) {
      const orgName = organizations.find(o => o.id === form.organization_id)?.name || form.organization_id;
      toast.error(`'${orgName}' already has a program named '${form.name}'. Rename one of them before moving.`);
      return;
    }
    
    // Prepare form data with metadata
    const formData = {
      name: form.name,
      type: form.type,
      description: form.description,
      status: form.status,
      cohort_ids: form.cohort_ids,
      metadata: {
        total_investment: form.total_investment ? parseFloat(form.total_investment) : 0,
        impact_per_percent: form.impact_per_percent ? parseFloat(form.impact_per_percent) : 1000
      }
    };
    if (form.organization_id) {
      formData.organization_id = form.organization_id;
    }
    
    setSaving(true);
    try {
      await onSave(formData, program?.id);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save program');
    }
    setSaving(false);
  };
  
  const toggleCohort = (cohortId) => {
    setForm(prev => ({
      ...prev,
      cohort_ids: prev.cohort_ids.includes(cohortId)
        ? prev.cohort_ids.filter(id => id !== cohortId)
        : [...prev.cohort_ids, cohortId]
    }));
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="program-dialog">
        <DialogHeader>
          <DialogTitle>{program ? 'Edit Program' : 'Create Program'}</DialogTitle>
          <DialogDescription>
            {program ? 'Update the program details' : 'Create a new learning program'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Organization - Super Admin: editable selector, Others: read-only display */}
          {isSuperAdmin && organizations.length > 0 ? (
            <div>
              <Label>Organization</Label>
              <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v, cohort_ids: [] })}>
                <SelectTrigger data-testid="program-org-selector">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {program?.organization_name && (
                <p className="text-xs text-gray-500 mt-1">Currently: {program.organization_name}</p>
              )}
              {duplicateInTargetOrg && (
                <div
                  className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2"
                  data-testid="program-duplicate-warning"
                >
                  <div className="flex-1">
                    <strong>Name conflict:</strong>{' '}
                    {organizations.find(o => o.id === form.organization_id)?.name || 'Target org'} already
                    has a program named <span className="font-mono">"{form.name}"</span>.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs border-amber-400 hover:bg-amber-100 shrink-0"
                    data-testid="program-rename-and-move-btn"
                    onClick={() => {
                      const sourceOrgName =
                        program?.organization_name ||
                        organizations.find(o => o.id === program?.organization_id)?.name ||
                        'Source';
                      const base = form.name.trim();
                      const suffix = ` (from ${sourceOrgName})`;
                      let candidate = `${base}${suffix}`;
                      const existsInTarget = (n) =>
                        allKnownPrograms.some(p =>
                          p.id !== program?.id &&
                          p.organization_id === form.organization_id &&
                          (p.name || '').trim().toLowerCase() === n.trim().toLowerCase()
                        );
                      let i = 2;
                      while (existsInTarget(candidate)) {
                        candidate = `${base}${suffix} ${i}`;
                        i += 1;
                      }
                      setForm(prev => ({ ...prev, name: candidate }));
                      toast.info(`Renamed to "${candidate}". Click Save Changes to move.`);
                    }}
                  >
                    Rename &amp; move
                  </Button>
                </div>
              )}
            </div>
          ) : program?.organization_name && (
            <div>
              <Label>Organization</Label>
              <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-gray-50 rounded-lg border" data-testid="program-org-display">
                <Building2 size={14} className="text-teal-500" />
                <span className="text-sm font-medium text-gray-700">{program.organization_name}</span>
              </div>
            </div>
          )}
          
          <div>
            <Label htmlFor="program-name">Name *</Label>
            <Input
              id="program-name"
              placeholder="e.g., Sales Onboarding 2026"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="program-name-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Input
                placeholder="e.g., onboarding, leadership"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAM_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="program-desc">Description</Label>
            <Textarea
              id="program-desc"
              placeholder="Optional description..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          
          {/* Investment Settings */}
          <div className="space-y-3 p-4 bg-gradient-to-r from-emerald-50 to-emerald-50 rounded-lg border border-emerald-100">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={18} className="text-emerald-600" />
              <Label className="text-emerald-800 font-semibold">ROI Settings</Label>
            </div>
            <p className="text-xs text-emerald-700 -mt-1 mb-3">
              Configure these values for accurate ROI calculation in Program Analytics
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total-investment" className="text-sm text-emerald-700">
                  Total Investment ($)
                </Label>
                <div className="relative mt-1">
                  <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="total-investment"
                    type="number"
                    min="0"
                    step="1000"
                    placeholder="e.g., 50000"
                    value={form.total_investment}
                    onChange={(e) => setForm({ ...form, total_investment: e.target.value })}
                    className="pl-8"
                    data-testid="program-investment-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Total program cost</p>
              </div>
              
              <div>
                <Label htmlFor="impact-per-percent" className="text-sm text-emerald-700">
                  Impact per 1% LEI ($)
                </Label>
                <div className="relative mt-1">
                  <TrendingUp size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="impact-per-percent"
                    type="number"
                    min="0"
                    step="100"
                    placeholder="e.g., 1000"
                    value={form.impact_per_percent}
                    onChange={(e) => setForm({ ...form, impact_per_percent: e.target.value })}
                    className="pl-8"
                    data-testid="program-impact-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Business value per 1% improvement</p>
              </div>
            </div>
            
            {/* ROI Preview */}
            {form.total_investment && form.impact_per_percent && (
              <div className="mt-3 p-2 bg-white rounded border border-emerald-200">
                <p className="text-xs text-gray-600">
                  <strong>Break-even at:</strong>{' '}
                  <span className="text-emerald-700 font-semibold">
                    +{(parseFloat(form.total_investment) / parseFloat(form.impact_per_percent)).toFixed(1)}% LEI improvement
                  </span>
                </p>
              </div>
            )}
          </div>
          
          {/* Cohort Selection */}
          <div>
            <Label>Link to Cohorts</Label>
            <p className="text-xs text-gray-500 mb-2">Select which cohorts this program applies to</p>
            {cohorts.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No cohorts available. Create cohorts first to link them to programs.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {cohorts.map(cohort => (
                  <label
                    key={cohort.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      form.cohort_ids.includes(cohort.id)
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.cohort_ids.includes(cohort.id)}
                      onChange={() => toggleCohort(cohort.id)}
                      className="rounded border-gray-300"
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <span className="font-medium text-sm">{cohort.name}</span>
                      <Badge variant="outline" className="ml-1 text-xs">{cohort.type}</Badge>
                      {cohort.organization_name && cohort.organization_id !== 'demo' && (
                        <Badge variant="outline" className="text-xs text-teal-600 border-teal-200">{cohort.organization_name}</Badge>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {program ? 'Save Changes' : 'Create Program'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export default function SegmentsPage() {
  const navigate = useNavigate();
  const { user, isSuperAdmin, viewAsOrg } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'programs' ? 'programs' : 'cohorts'));
  
  // Data
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  
  // Demo data toggle - persist preference in localStorage
  const [showDemoData, setShowDemoData] = useState(() => {
    const saved = localStorage.getItem('showDemoData');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [cohortTypeFilter, setCohortTypeFilter] = useState('all');
  const [programStatusFilter, setProgramStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  
  // Dialogs
  const [cohortDialog, setCohortDialog] = useState({ open: false, cohort: null });
  const [rosterDialog, setRosterDialog] = useState({ open: false, cohort: null });
  const [lmsSyncOpen, setLmsSyncOpen] = useState(false);
  const [programDialog, setProgramDialog] = useState({ open: false, program: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: null });
  // Bulk selection + move state (super admin only)
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [bulkMoveDialog, setBulkMoveDialog] = useState({ open: false, type: null });
  const [bulkMoveForm, setBulkMoveForm] = useState({ target_organization_id: '', rename_on_conflict: true });
  const [bulkMoving, setBulkMoving] = useState(false);

  const toggleCohortSelect = (id) => {
    setSelectedCohortIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleProgramSelect = (id) => {
    setSelectedProgramIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const clearSelection = (type) => {
    if (type === 'cohort') setSelectedCohortIds([]);
    else setSelectedProgramIds([]);
  };

  const runBulkMove = async () => {
    const type = bulkMoveDialog.type;
    const ids = type === 'cohort' ? selectedCohortIds : selectedProgramIds;
    if (!ids.length || !bulkMoveForm.target_organization_id) return;
    setBulkMoving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const endpoint = type === 'cohort'
        ? `${API_URL}/api/segments/cohorts/bulk-move`
        : `${API_URL}/api/segments/programs/bulk-move`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids,
          target_organization_id: bulkMoveForm.target_organization_id,
          rename_on_conflict: bulkMoveForm.rename_on_conflict,
        })
      });
      const raw = await res.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch (parseErr) {
        logger.debug('Non-JSON bulk-move response, ignoring:', parseErr);
      }
      if (!res.ok) {
        throw new Error(body.detail || `Bulk move failed (HTTP ${res.status})`);
      }
      toast.success(body.summary || `Moved ${body.moved?.length || 0} item(s)`);
      if (body.conflicts?.length) {
        toast.warning(`${body.conflicts.length} skipped. Enable "Rename on conflict" to resolve.`);
      }
      setBulkMoveDialog({ open: false, type: null });
      clearSelection(type);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Bulk move failed');
    } finally {
      setBulkMoving(false);
    }
  };
  const [importDialog, setImportDialog] = useState({ open: false, type: null });
  const [demographicsImportDialog, setDemographicsImportDialog] = useState(false);
  
  // CSV Import state
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const demographicsFileInputRef = useRef(null);
  
  // CSV Import handler
  const handleCSVImport = async (file, type) => {
    if (!file) return;
    
    setImporting(true);
    const token = secureStorage.getItem('session_token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await authedFetch(`${API_URL}/api/segments/${type}/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        if (data.errors?.length > 0) {
          data.errors.slice(0, 3).forEach(err => toast.warning(err));
        }
        fetchData();
        setImportDialog({ open: false, type: null });
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Demographics CSV Import state
  const [validationPreview, setValidationPreview] = useState(null);
  const [strictMode, setStrictMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Demographics CSV Validation handler
  const handleDemographicsValidation = async (file) => {
    if (!file) return;
    
    setSelectedFile(file);
    setImporting(true);
    const token = secureStorage.getItem('session_token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await authedFetch(`${API_URL}/api/segments/cohorts/validate-demographics-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setValidationPreview(data);
      } else {
        toast.error(data.detail || 'Validation failed');
      }
    } catch (error) {
      toast.error('Validation failed: ' + error.message);
    } finally {
      setImporting(false);
    }
  };
  
  // Demographics CSV Import handler (after validation)
  const handleDemographicsImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    const token = secureStorage.getItem('session_token');
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await authedFetch(`${API_URL}/api/segments/cohorts/import-demographics-csv?strict_mode=${strictMode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        if (data.warnings?.length > 0) {
          const errorWarnings = data.warnings.filter(w => w.severity === 'error');
          const normalWarnings = data.warnings.filter(w => w.severity === 'warning');
          if (errorWarnings.length > 0) {
            toast.error(`${errorWarnings.length} validation errors found`);
          }
          if (normalWarnings.length > 0) {
            toast.warning(`${normalWarnings.length} validation warnings`);
          }
        }
        fetchData();
        setDemographicsImportDialog(false);
        setValidationPreview(null);
        setSelectedFile(null);
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
      if (demographicsFileInputRef.current) demographicsFileInputRef.current.value = '';
    }
  };
  
  // Reset validation preview when dialog closes
  const handleDemographicsDialogClose = (open) => {
    if (!open) {
      setValidationPreview(null);
      setSelectedFile(null);
      setStrictMode(false);
    }
    setDemographicsImportDialog(open);
  };
  
  // Save demo data preference
  useEffect(() => {
    localStorage.setItem('showDemoData', JSON.stringify(showDemoData));
  }, [showDemoData]);
  
  // Define fetchData BEFORE the useEffect that uses it
  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = secureStorage.getItem('session_token');
    const demoParam = `include_demo=${showDemoData}`;
    // When viewing as a specific org, scope to that org; otherwise use the page filter
    const effectiveOrgFilter = viewAsOrg ? viewAsOrg.id : orgFilter;
    const orgParam = isSuperAdmin && effectiveOrgFilter !== 'all' ? `&organization_id=${effectiveOrgFilter}` : isSuperAdmin ? `&organization_id=all` : '';
    
    // Build common headers including View-As-Org
    const headers = { 'Authorization': `Bearer ${token}` };
    if (viewAsOrg?.id) {
      headers['X-View-As-Org'] = viewAsOrg.id;
    }
    
    try {
      const [cohortsRes, programsRes, summaryRes] = await Promise.all([
        authedFetch(`${API_URL}/api/segments/cohorts?${demoParam}${orgParam}`, { headers }),
        authedFetch(`${API_URL}/api/segments/programs?${demoParam}${orgParam}`, { headers }),
        authedFetch(`${API_URL}/api/segments/summary`, { headers })
      ]);
      
      if (cohortsRes.ok) {
        const data = await cohortsRes.json();
        setCohorts(data.cohorts || []);
      }
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.programs || []);
      }
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      logger.error('Failed to fetch segments:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, [showDemoData, isSuperAdmin, orgFilter, viewAsOrg]);
  
  // Now the useEffect can safely reference fetchData
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch orgs list for Super Admin filter
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  const saveCohort = async (data, cohortId) => {
    const token = secureStorage.getItem('session_token');
    const url = cohortId
      ? `${API_URL}/api/segments/cohorts/${cohortId}`
      : `${API_URL}/api/segments/cohorts`;
    
    const response = await fetch(url, {
      method: cohortId ? 'PUT' : 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      // Read body as text first so a subsequent JSON parse can't fail with
      // "body stream already read" — the stream can only be consumed once.
      let detail = `Failed to save cohort (HTTP ${response.status})`;
      try {
        const raw = await response.text();
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            detail = parsed.detail || parsed.message || raw.slice(0, 200);
          } catch {
            detail = raw.slice(0, 200);
          }
        }
      } catch { /* response body unavailable */ }
      throw new Error(detail);
    }
    
    toast.success(cohortId ? 'Cohort updated!' : 'Cohort created!');
    fetchData();
  };
  
  const saveProgram = async (data, programId) => {
    const token = secureStorage.getItem('session_token');
    const url = programId
      ? `${API_URL}/api/segments/programs/${programId}`
      : `${API_URL}/api/segments/programs`;
    
    const response = await fetch(url, {
      method: programId ? 'PUT' : 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      let detail = `Failed to save program (HTTP ${response.status})`;
      try {
        const raw = await response.text();
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            detail = parsed.detail || parsed.message || raw.slice(0, 200);
          } catch {
            detail = raw.slice(0, 200);
          }
        }
      } catch { /* response body unavailable */ }
      throw new Error(detail);
    }
    
    toast.success(programId ? 'Program updated!' : 'Program created!');
    fetchData();
  };
  
  const handleDelete = async () => {
    const { item, type } = deleteDialog;
    if (!item || !type) return;
    
    const token = secureStorage.getItem('session_token');
    const url = `${API_URL}/api/segments/${type}s/${item.id}`;
    
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`${type === 'cohort' ? 'Cohort' : 'Program'} deleted!`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
    
    setDeleteDialog({ open: false, item: null, type: null });
  };
  
  // Filter data
  const filteredCohorts = cohorts.filter(c => {
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (cohortTypeFilter !== 'all' && c.type !== cohortTypeFilter) return false;
    return true;
  });
  
  const filteredPrograms = programs.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (programStatusFilter !== 'all' && p.status !== programStatusFilter) return false;
    return true;
  });
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }
  
  const __pageTheme = getLightSectionTheme('Navy');
  const segmentsHeroActions = (
    <Button
      onClick={() => navigate('/component-data-import')}
      className={__pageTheme.actionBtn}
      data-testid="segments-import-btn"
    >
      Import Data
    </Button>
  );
  const segmentsHeroStats = summary ? (() => {
    const cohortTotal = summary.cohorts?.total || 0;
    const cohortByType = summary.cohorts?.by_type || {};
    const cohortHintParts = [
      cohortByType.ongoing ? `${cohortByType.ongoing} ongoing` : null,
      cohortByType.time_bound ? `${cohortByType.time_bound} time-bound` : null,
      cohortByType.project ? `${cohortByType.project} project` : null,
      cohortByType.custom ? `${cohortByType.custom} custom` : null,
    ].filter(Boolean);
    const cohortHint = cohortHintParts.length ? cohortHintParts.slice(0, 2).join(' · ') : 'No cohorts yet';

    const programTotal = summary.programs?.total || 0;
    const programByStatus = summary.programs?.by_status || {};
    const programHintParts = [
      programByStatus.active ? `${programByStatus.active} active` : null,
      programByStatus.draft ? `${programByStatus.draft} draft` : null,
      programByStatus.completed ? `${programByStatus.completed} completed` : null,
      programByStatus.archived ? `${programByStatus.archived} archived` : null,
    ].filter(Boolean);
    const programHint = programHintParts.length ? programHintParts.slice(0, 2).join(' · ') : 'No programs yet';

    const withCohorts = summary.data_segmentation?.with_cohorts || 0;
    const withPrograms = summary.data_segmentation?.with_programs || 0;
    const taggedHint =
      withCohorts && withPrograms
        ? 'with cohort + program assignment'
        : withCohorts
        ? 'with cohort assignment'
        : withPrograms
        ? 'with program assignment'
        : 'No segmented data yet';
    const taggedTotal = Math.max(withCohorts, withPrograms);

    return [
      {
        label: 'Cohorts',
        value: cohortTotal,
        hint: cohortHint,
        testId: 'segments-stat-cohorts',
      },
      {
        label: 'Programs',
        value: programTotal,
        hint: programHint,
        testId: 'segments-stat-programs',
      },
      {
        label: 'Tagged LEI records',
        value: taggedTotal,
        hint: taggedHint,
        testId: 'segments-stat-tagged',
      },
    ];
  })() : null;
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Data Segmentation"
        description="Organize your LEI data by cohorts (groups) and programs (learning initiatives). Segmentation enables granular analysis and comparison across different populations."
        theme={__pageTheme}
        icon={Layers}
        actions={segmentsHeroActions}
        stats={segmentsHeroStats}
        testId="segments-hero"
      />
    )}>
    <div className="min-h-screen bg-gray-50" data-testid="segments-page">
      <div className="w-full px-6 py-8 lg:px-10">

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="h-auto p-1 bg-slate-100">
              <TabsTrigger value="cohorts" className="flex items-center gap-2 py-2 px-4">
                <Users size={16} />
                Cohorts ({cohorts.length})
              </TabsTrigger>
              <TabsTrigger value="programs" className="flex items-center gap-2 py-2 px-4">
                <GraduationCap size={16} />
                Programs ({programs.length})
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-4">
              {/* Demo Data Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border shadow-sm">
                <label 
                  htmlFor="show-demo-toggle" 
                  className="text-sm font-medium text-gray-700 flex items-center gap-1.5 cursor-pointer"
                >
                  {showDemoData ? <Eye size={16} className="text-amber-500" /> : <EyeOff size={16} className="text-gray-400" />}
                  Sample Data
                </label>
                <Switch
                  id="show-demo-toggle"
                  checked={showDemoData}
                  onCheckedChange={setShowDemoData}
                  data-testid="show-demo-toggle"
                />
              </div>
              
              {/* Import Demographics Button (only for cohorts tab) */}
              {activeTab === 'cohorts' && (
                <Button
                  onClick={() => setDemographicsImportDialog(true)}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="import-demographics-btn"
                >
                  <Users size={16} className="mr-2" />
                  Import Demographics
                </Button>
              )}
              
              {/* Import CSV Button */}
              <Button
                onClick={() => setImportDialog({ open: true, type: activeTab })}
                variant="outline"
                className="border-gray-300"
                data-testid={`import-${activeTab}-btn`}
              >
                <Upload size={16} className="mr-2" />
                Import CSV
              </Button>
              
              {activeTab === 'cohorts' && (
                <Button variant="outline" onClick={() => setLmsSyncOpen(true)} data-testid="lms-sync-btn">
                  <RefreshCw size={16} className="mr-2" />
                  Sync from LMS
                </Button>
              )}
              <Button
                onClick={() => activeTab === 'cohorts' 
                  ? setCohortDialog({ open: true, cohort: null })
                  : setProgramDialog({ open: true, program: null })
                }
                className={activeTab === 'cohorts' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}
                data-testid={`create-${activeTab === 'cohorts' ? 'cohort' : 'program'}-btn`}
              >
                <Plus size={16} className="mr-2" />
                {activeTab === 'cohorts' ? 'New Cohort' : 'New Program'}
              </Button>
            </div>
          </div>
          
          {/* Search & Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="segments-search"
              />
            </div>
            
            {activeTab === 'cohorts' && <LearnerSearch organizationId={viewAsOrg?.id || orgFilter} />}

            {isSuperAdmin && organizations.length > 0 && (
              <Select value={orgFilter} onValueChange={setOrgFilter}>
                <SelectTrigger className="w-48" data-testid="segments-org-filter">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {activeTab === 'cohorts' && (
              <Select value={cohortTypeFilter} onValueChange={setCohortTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {COHORT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {activeTab === 'programs' && (
              <Select value={programStatusFilter} onValueChange={setProgramStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {PROGRAM_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {/* Cohorts Tab */}
          <TabsContent value="cohorts">
            {filteredCohorts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Users className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Cohorts Yet</h3>
                  <p className="text-gray-500 mb-4">
                    Create cohorts to segment your data by teams, departments, or time periods.
                  </p>
                  <Button
                    onClick={() => setCohortDialog({ open: true, cohort: null })}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus size={16} className="mr-2" />
                    Create First Cohort
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredCohorts.map(cohort => (
                  <SegmentCard
                    key={cohort.id}
                    item={cohort}
                    type="cohort"
                    programs={programs}
                    onEdit={() => setCohortDialog({ open: true, cohort })}
                    onRoster={() => setRosterDialog({ open: true, cohort })}
                    onDelete={() => setDeleteDialog({ open: true, item: cohort, type: 'cohort' })}
                    selectable={isSuperAdmin && cohort.organization_id !== 'demo'}
                    selected={selectedCohortIds.includes(cohort.id)}
                    onToggleSelect={toggleCohortSelect}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Programs Tab */}
          <TabsContent value="programs">
            {filteredPrograms.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <GraduationCap className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Programs Yet</h3>
                  <p className="text-gray-500 mb-4">
                    Create programs to track learning initiatives and link them to cohorts.
                  </p>
                  <Button
                    onClick={() => setProgramDialog({ open: true, program: null })}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus size={16} className="mr-2" />
                    Create First Program
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredPrograms.map(program => (
                  <SegmentCard
                    key={program.id}
                    item={program}
                    type="program"
                    cohorts={cohorts}
                    onEdit={() => setProgramDialog({ open: true, program })}
                    onDelete={() => setDeleteDialog({ open: true, item: program, type: 'program' })}
                    selectable={isSuperAdmin && program.organization_id !== 'demo'}
                    selected={selectedProgramIds.includes(program.id)}
                    onToggleSelect={toggleProgramSelect}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk action bar — appears when any cohort/program is selected by a super admin */}
      {isSuperAdmin && (selectedCohortIds.length > 0 || selectedProgramIds.length > 0) && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
          data-testid="bulk-action-bar"
        >
          <div className="w-full px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
            <div className="text-sm text-slate-700">
              {selectedCohortIds.length > 0 && (
                <span className="mr-4">
                  <strong>{selectedCohortIds.length}</strong> cohort{selectedCohortIds.length !== 1 ? 's' : ''} selected
                </span>
              )}
              {selectedProgramIds.length > 0 && (
                <span>
                  <strong>{selectedProgramIds.length}</strong> program{selectedProgramIds.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedCohortIds.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => setBulkMoveDialog({ open: true, type: 'cohort' })}
                  data-testid="bulk-move-cohorts-btn"
                >
                  Move {selectedCohortIds.length} cohort{selectedCohortIds.length !== 1 ? 's' : ''}…
                </Button>
              )}
              {selectedProgramIds.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => setBulkMoveDialog({ open: true, type: 'program' })}
                  data-testid="bulk-move-programs-btn"
                >
                  Move {selectedProgramIds.length} program{selectedProgramIds.length !== 1 ? 's' : ''}…
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setSelectedCohortIds([]); setSelectedProgramIds([]); }}
                data-testid="bulk-clear-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Move dialog */}
      <Dialog open={bulkMoveDialog.open} onOpenChange={(o) => !o && setBulkMoveDialog({ open: false, type: null })}>
        <DialogContent className="max-w-md" data-testid="bulk-move-dialog">
          <DialogHeader>
            <DialogTitle>
              Move {bulkMoveDialog.type === 'cohort' ? selectedCohortIds.length : selectedProgramIds.length}{' '}
              {bulkMoveDialog.type}{((bulkMoveDialog.type === 'cohort' ? selectedCohortIds.length : selectedProgramIds.length) !== 1) ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target organization</Label>
              <Select
                value={bulkMoveForm.target_organization_id}
                onValueChange={(v) => setBulkMoveForm(f => ({ ...f, target_organization_id: v }))}
              >
                <SelectTrigger data-testid="bulk-move-org-selector">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-slate-200 p-3 bg-slate-50">
              <input
                id="bulk-rename-on-conflict"
                type="checkbox"
                checked={bulkMoveForm.rename_on_conflict}
                onChange={(e) => setBulkMoveForm(f => ({ ...f, rename_on_conflict: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-teal-600"
                data-testid="bulk-rename-toggle"
              />
              <label htmlFor="bulk-rename-on-conflict" className="text-sm text-slate-700 cursor-pointer">
                <strong>Rename on conflict</strong>
                <div className="text-xs text-slate-500 mt-0.5">
                  If the target org already has an item with the same name, auto-suffix with
                  &quot;(from &lt;source org&gt;)&quot;. Otherwise the item is skipped.
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkMoveDialog({ open: false, type: null })} disabled={bulkMoving}>
              Cancel
            </Button>
            <Button
              onClick={runBulkMove}
              disabled={bulkMoving || !bulkMoveForm.target_organization_id}
              data-testid="bulk-move-confirm-btn"
            >
              {bulkMoving ? 'Moving…' : 'Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialogs */}
      <CohortDialog
        open={cohortDialog.open}
        onClose={() => setCohortDialog({ open: false, cohort: null })}
        cohort={cohortDialog.cohort}
        onSave={saveCohort}
        organizations={organizations}
        programs={programs}
        isSuperAdmin={isSuperAdmin}
        existingCohorts={cohorts}
      />
      
      <ProgramDialog
        open={programDialog.open}
        onClose={() => setProgramDialog({ open: false, program: null })}
        program={programDialog.program}
        cohorts={cohorts}
        onSave={saveProgram}
        organizations={organizations}
        isSuperAdmin={isSuperAdmin}
        existingPrograms={programs}
      />
      
      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={() => setDeleteDialog({ open: false, item: null, type: null })}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.type === 'cohort' ? 'Cohort' : 'Program'}?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.item?.name}"? 
              This will remove it from any associated data imports.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null, type: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CSV Import Dialog */}
      <Dialog open={importDialog.open} onOpenChange={() => setImportDialog({ open: false, type: null })}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Import {importDialog.type === 'cohorts' ? 'Cohorts' : 'Programs'} from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import {importDialog.type === 'cohorts' ? 'cohorts' : 'programs'}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Sample Data Downloads */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <Download size={16} />
                Download Sample CSV
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                Download a pre-filled sample CSV to see the expected format:
              </p>
              <div className="flex flex-wrap gap-2">
                {importDialog.type === 'cohorts' ? (
                  <a
                    href="/sample-data/sample_cohorts.csv"
                    download="sample_cohorts.csv"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 rounded-lg text-amber-800 hover:bg-amber-50 text-sm font-medium"
                    data-testid="download-sample-cohorts"
                  >
                    <FileSpreadsheet size={16} />
                    Sample Cohorts (10 rows)
                  </a>
                ) : (
                  <a
                    href="/sample-data/sample_programs.csv"
                    download="sample_programs.csv"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 rounded-lg text-amber-800 hover:bg-amber-50 text-sm font-medium"
                    data-testid="download-sample-programs"
                  >
                    <FileSpreadsheet size={16} />
                    Sample Programs (10 rows)
                  </a>
                )}
                <a
                  href={`${API_URL}/api/segments/${importDialog.type}/template-csv`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  <FileSpreadsheet size={16} />
                  Blank Template
                </a>
              </div>
            </div>
            
            {/* CSV Format Info */}
            <div className="bg-gray-50 rounded-lg p-4 border">
              <h4 className="font-semibold text-gray-800 mb-2">Expected CSV Format</h4>
              <div className="bg-white rounded border p-3 font-mono text-xs overflow-x-auto">
                {importDialog.type === 'cohorts' ? (
                  <>
                    <p className="text-gray-500">name,type,description</p>
                    <p>Marketing Team,ongoing,All marketing members</p>
                    <p>Q2 New Hires,time_bound,Q2 2026 onboarding cohort</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500">name,type,status,description,total_investment,impact_per_percent</p>
                    <p>Sales Training,sales,active,Sales program,50000,1500</p>
                    <p>Leadership Dev,leadership,draft,Leaders program,100000,2000</p>
                  </>
                )}
              </div>
            </div>
            
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                id="csv-file-input"
                onChange={(e) => handleCSVImport(e.target.files?.[0], importDialog.type)}
              />
              <label htmlFor="csv-file-input" className="cursor-pointer">
                <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-400 mt-1">CSV files only</p>
              </label>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setImportDialog({ open: false, type: null })}
              disabled={importing}
              className="flex-shrink-0"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex-shrink-0"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Select File
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Demographics Import Dialog */}
      <Dialog open={demographicsImportDialog} onOpenChange={handleDemographicsDialogClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} className="text-teal-600" />
              Import Cohort Demographics from CSV
            </DialogTitle>
            <DialogDescription>
              Bulk import population size, department breakdown, regions, tenure levels, and role distributions for cohorts.
              Creates new cohorts if they don't exist, updates demographics if they do.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Validation Preview (shown after file selection) */}
            {validationPreview && (
              <div className="space-y-4">
                {/* Summary */}
                <div className={`rounded-lg p-4 border ${
                  validationPreview.error_count > 0 ? 'bg-rose-50 border-rose-200' :
                  validationPreview.warning_count > 0 ? 'bg-amber-50 border-amber-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold flex items-center gap-2 ${
                      validationPreview.error_count > 0 ? 'text-rose-800' :
                      validationPreview.warning_count > 0 ? 'text-amber-800' :
                      'text-emerald-800'
                    }`}>
                      {validationPreview.error_count > 0 ? (
                        <><AlertCircle size={18} /> Validation Issues Found</>
                      ) : validationPreview.warning_count > 0 ? (
                        <><AlertCircle size={18} /> Validation Warnings</>
                      ) : (
                        <><CheckCircle size={18} /> Validation Passed</>
                      )}
                    </h4>
                    <Badge variant="outline" className="text-xs">
                      {validationPreview.format_detected} format
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-2xl font-bold text-gray-900">{validationPreview.total_cohorts}</div>
                      <div className="text-xs text-gray-500">Total Cohorts</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-2xl font-bold text-blue-600">{validationPreview.will_create}</div>
                      <div className="text-xs text-gray-500">To Create</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-2xl font-bold text-purple-600">{validationPreview.will_update}</div>
                      <div className="text-xs text-gray-500">To Update</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className={`text-2xl font-bold ${validationPreview.error_count > 0 ? 'text-rose-600' : validationPreview.warning_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {validationPreview.error_count + validationPreview.warning_count}
                      </div>
                      <div className="text-xs text-gray-500">Issues</div>
                    </div>
                  </div>
                </div>
                
                {/* Validation Warnings List */}
                {validationPreview.warnings.length > 0 && (
                  <div className="bg-white rounded-lg border p-3">
                    <h5 className="font-semibold text-gray-800 text-sm mb-2">Validation Details</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {validationPreview.warnings.map((w, idx) => (
                        <div key={`warn-${w.cohort}-${w.severity}`} className={`flex items-start gap-2 text-sm p-2 rounded ${
                          w.severity === 'error' ? 'bg-rose-50 text-rose-800' : 'bg-amber-50 text-amber-800'
                        }`}>
                          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-medium">{w.cohort}:</span> {w.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Parse Errors */}
                {validationPreview.parse_errors.length > 0 && (
                  <div className="bg-rose-50 rounded-lg border border-rose-200 p-3">
                    <h5 className="font-semibold text-rose-800 text-sm mb-2">Parse Errors</h5>
                    <div className="space-y-1 text-sm text-rose-700">
                      {validationPreview.parse_errors.map((err, idx) => (
                        <div key={`err-${err.slice(0, 20)}-${idx}`}>{err}</div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Strict Mode Toggle */}
                {validationPreview.error_count > 0 && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex-1">
                      <Label htmlFor="strict-mode" className="font-medium">Strict Mode</Label>
                      <p className="text-xs text-gray-500 mt-1">
                        {strictMode 
                          ? 'Enabled: Cohorts with validation errors will be skipped'
                          : 'Disabled: All cohorts will be imported (errors become warnings)'}
                      </p>
                    </div>
                    <Switch
                      id="strict-mode"
                      checked={strictMode}
                      onCheckedChange={setStrictMode}
                    />
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setValidationPreview(null);
                      setSelectedFile(null);
                      if (demographicsFileInputRef.current) demographicsFileInputRef.current.value = '';
                    }}
                    className="flex-1"
                  >
                    <X size={16} className="mr-2" />
                    Select Different File
                  </Button>
                  <Button 
                    onClick={handleDemographicsImport}
                    disabled={importing || (strictMode && validationPreview.error_count > 0 && validationPreview.will_create + validationPreview.will_update === validationPreview.error_count)}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                  >
                    {importing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing...</>
                    ) : (
                      <><CheckCircle size={16} className="mr-2" /> Import {validationPreview.total_cohorts} Cohorts</>
                    )}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Initial View (before file selection) */}
            {!validationPreview && (
              <>
                {/* Format Selection & Templates */}
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200">
                  <h4 className="font-semibold text-teal-800 mb-2 flex items-center gap-2">
                    <Download size={16} />
                    Download CSV Templates
                  </h4>
                  <p className="text-sm text-teal-700 mb-3">
                    Two formats supported - choose the one that matches your HR export:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href={`${API_URL}/api/segments/cohorts/demographics-template-csv?format=wide`}
                      className="flex flex-col p-3 bg-white border border-teal-300 rounded-lg text-teal-800 hover:bg-teal-50 transition-colors"
                      data-testid="download-demographics-template-wide"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <FileSpreadsheet size={18} />
                        Wide Format
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        One row per cohort, columns for each demographic category
                      </p>
                    </a>
                    <a
                      href={`${API_URL}/api/segments/cohorts/demographics-template-csv?format=long`}
                      className="flex flex-col p-3 bg-white border border-teal-300 rounded-lg text-teal-800 hover:bg-teal-50 transition-colors"
                      data-testid="download-demographics-template-long"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <FileSpreadsheet size={18} />
                        Long Format
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Multiple rows per cohort, easier from HR system exports
                      </p>
                    </a>
                  </div>
                </div>
                
                {/* Validation Info */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <h5 className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Data Validation (±10% tolerance)
                  </h5>
                  <div className="text-xs text-amber-700 space-y-1">
                    <p>• Population cannot exceed total workforce</p>
                    <p>• Department/Region/Tenure/Role sums should match population (±10%)</p>
                    <p>• Files are validated before import - you can review issues and choose strict mode</p>
                  </div>
                </div>
                
                {/* Format Examples */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 border">
                    <h5 className="font-semibold text-gray-800 text-sm mb-2">Wide Format Example</h5>
                    <div className="bg-white rounded border p-2 font-mono text-xs overflow-x-auto">
                      <p className="text-gray-500">cohort_name,population_size,dept_Sales,region_NA,role_IC</p>
                      <p>Sales Team,250,150,180,175</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border">
                    <h5 className="font-semibold text-gray-800 text-sm mb-2">Long Format Example</h5>
                    <div className="bg-white rounded border p-2 font-mono text-xs overflow-x-auto">
                      <p className="text-gray-500">cohort_name,category_type,category_name,count</p>
                      <p>Sales Team,population,,250</p>
                      <p>Sales Team,department,Sales,150</p>
                    </div>
                  </div>
                </div>
                
                {/* File Upload */}
                <div className="border-2 border-dashed border-teal-300 rounded-lg p-6 text-center hover:border-teal-500 bg-teal-50/30 transition-colors">
                  <input
                    ref={demographicsFileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    id="demographics-csv-file-input"
                    onChange={(e) => handleDemographicsValidation(e.target.files?.[0])}
                  />
                  <label htmlFor="demographics-csv-file-input" className="cursor-pointer">
                    <Users className="mx-auto h-10 w-10 text-teal-500 mb-2" />
                    <p className="text-sm text-gray-600">
                      <span className="text-teal-600 font-medium">Click to upload</span> demographics CSV
                    </p>
                    <p className="text-xs text-gray-400 mt-1">File will be validated before import</p>
                  </label>
                </div>
              </>
            )}
          </div>
          
          {!validationPreview && (
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => handleDemographicsDialogClose(false)}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => demographicsFileInputRef.current?.click()}
                disabled={importing}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Select File
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      <LmsRosterSyncDialog open={lmsSyncOpen} onOpenChange={setLmsSyncOpen} organizationId={orgFilter} />
      <CohortRosterDialog
        cohort={rosterDialog.cohort}
        open={rosterDialog.open}
        onOpenChange={(o) => { if (!o) setRosterDialog({ open: false, cohort: null }); }}
      />
    </div>
    </PageShell>
  );
}
